import React from 'react';
import GroupedFields from '../GroupedFields';
import { FieldGroupAttrs, UNPLACED_TAB } from '../../utils/fieldGroups';
import { LayoutEditor } from './useLayoutEditor';

// 「页签 → 分组」编辑的两块通用 UI (与 useLayoutEditor 配套; 参数模板 / 设备字段组编辑器共用).

const itemAttrs = <F extends FieldGroupAttrs>(it: { f: F }) => it.f;
const itemKey = (it: { idx: number }) => it.idx;

/** 当前页签的操作: 左移/右移、改名、删除、+ 新页签 (放在字段区标题行右侧). */
export function LayoutTabActions<F extends FieldGroupAttrs>({ ed, canUpdate }: { ed: LayoutEditor<F>; canUpdate: boolean }) {
    if (!ed.grouped || !canUpdate) return null;
    const tab = ed.activeTab;
    return (
        <div className="flex items-center gap-3 text-xs">
            <span className="inline-flex rounded border border-slate-200 overflow-hidden">
                <button
                    type="button"
                    className="px-2 py-1 text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"
                    title={`把「${tab}」往左挪一位`}
                    disabled={ed.tabPos <= 0}
                    onClick={() => ed.moveTab(-1)}
                >
                    ‹ 左移
                </button>
                <button
                    type="button"
                    className="px-2 py-1 text-slate-600 border-l border-slate-200 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"
                    title={`把「${tab}」往右挪一位`}
                    disabled={ed.tabPos < 0 || ed.tabPos >= (ed.layout?.length || 0) - 1}
                    onClick={() => ed.moveTab(1)}
                >
                    右移 ›
                </button>
            </span>
            {tab !== UNPLACED_TAB && (
                <button type="button" className="text-brand-600 hover:underline" onClick={() => ed.rename(tab)}>
                    改名
                </button>
            )}
            {tab !== UNPLACED_TAB && (
                <button type="button" className="text-rose-500 hover:underline" onClick={() => ed.remove(tab)}>
                    删除
                </button>
            )}
            <button type="button" className="text-brand-600 hover:underline" onClick={ed.newTab}>
                + 新页签
            </button>
        </div>
    );
}

/** 字段清单: 有分组按 页签 → 分组 嵌套 (分组可折叠, 标题旁 改名/删除/排序/+ 添加字段), 无分组平铺 + 「建页签」入口. */
export function LayoutFieldList<F extends FieldGroupAttrs>({
    ed,
    canUpdate,
    renderItem,
    gridClassName = 'space-y-2',
    hint = '建页签后按 页签 → 分组 嵌套展示',
}: {
    ed: LayoutEditor<F>;
    canUpdate: boolean;
    renderItem: (it: { f: F; idx: number }) => React.ReactNode;
    gridClassName?: string;
    hint?: string;
}) {
    if (!ed.grouped) {
        return (
            <>
                {ed.items.length > 0 && (
                    <div className="text-[11px] text-slate-400 flex items-center gap-2">
                        {hint}
                        {canUpdate && (
                            <button type="button" className="text-brand-600 hover:underline" onClick={ed.newTab}>
                                + 新页签
                            </button>
                        )}
                    </div>
                )}
                <div className={gridClassName}>
                    {ed.items.map((it) => (
                        <React.Fragment key={it.idx}>{renderItem(it)}</React.Fragment>
                    ))}
                </div>
            </>
        );
    }
    return (
        <GroupedFields
            items={ed.items}
            attrs={itemAttrs}
            layout={ed.layout}
            showEmpty
            itemKey={itemKey}
            renderItem={renderItem}
            gridClassName={gridClassName}
            onTabChange={ed.setActiveTab}
            focusTab={ed.focusTab}
            tabBarClassName="sticky top-0 z-10 bg-white"
            collapsible
            defaultCollapsed
            openSection={ed.openSection}
            belowTabs={(tab) =>
                canUpdate && tab !== UNPLACED_TAB ? (
                    <div className="mb-3">
                        <button
                            type="button"
                            className="w-full border border-dashed border-slate-300 rounded py-1.5 text-xs text-brand-600 hover:bg-brand-50"
                            onClick={() => ed.newSection(tab)}
                        >
                            + 新分组
                        </button>
                    </div>
                ) : null
            }
            sectionExtra={(tab, sec) =>
                canUpdate ? (
                    <div className="flex items-center gap-3 text-xs">
                        <button type="button" className="text-brand-600 hover:underline" title="上移" onClick={() => ed.moveSection(tab, sec, -1)}>
                            ↑
                        </button>
                        <button type="button" className="text-brand-600 hover:underline" title="下移" onClick={() => ed.moveSection(tab, sec, 1)}>
                            ↓
                        </button>
                        {sec && (
                            <button type="button" className="text-brand-600 hover:underline" onClick={() => ed.rename(tab, sec)}>
                                改名
                            </button>
                        )}
                        <button type="button" className="text-rose-500 hover:underline" onClick={() => ed.remove(tab, sec)}>
                            删除
                        </button>
                        <button type="button" className="text-brand-600 hover:underline" onClick={() => ed.addField(tab, sec)}>
                            + 添加字段
                        </button>
                    </div>
                ) : null
            }
        />
    );
}
